import React, { useEffect, useState } from "react";
import { Form, Input, Button, Typography, App } from "antd";
import QRCode from "qrcode";
import { authClient } from "../../api_actions/auth_index";
//import { apiClient } from '../../api_actions/index';
import { useNavigate, useLocation } from 'react-router-dom'
import { useCustomerLogoStore, useSmallCustomerLogoStore,useCustomerNameStore, useRoleNameStore, useIsValidator } from '../../store';
import "./Login.scss";
import { apiClient } from "../../api_actions";
import { REGEX_PATTERNS } from '../../constants/enums';
import { useParams } from 'react-router-dom';

const { Text, Title, Link } = Typography;

export default function LoginBox() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("login"); // login, code, newPassword, setupTotp, totpcode
  const [email, setEmail] = useState("");
  const [resetMode, setResetMode] = useState(false); // tracks if we're in forgot password flow
  const [secure_hash, setSecureHash] = useState("");
  const [hash_timed_out, setHashTimedOut] = useState("")
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpError, setTotpError] = useState("");
  const [loginError, setLoginError] = useState("");
  const isValidator = useIsValidator();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  const { hash } = useParams()

  useEffect(() => {
    if (!hash) {
      return // Exit early if hash is null, undefined, or empty string
    }

    const firstPart = hash.substring(0, hash.lastIndexOf('-'));  // "aa-b"
    setSecureHash(firstPart)

    const secondPart = hash.substring(hash.lastIndexOf('-') + 1);  // "rr"
    const timestampMs = parseInt(secondPart) * 1000;  // Unix is in seconds, JS uses ms
    const currentMs = Date.now();
    const fifteenMinutesMs = 15 * 60 * 1000;  // 15 minutes in milliseconds

    const isOver15MinutesOld = (currentMs - timestampMs) > fifteenMinutesMs;
    setHashTimedOut(isOver15MinutesOld)
    
    setStage("newPassword")
  }, [hash])

  const updateCustomerLogo = useCustomerLogoStore(state => state.updateCustomerLogo);
  const updateSmallCustomerLogo = useSmallCustomerLogoStore(state => state.updateSmallCustomerLogo)
  const updateCustomerName = useCustomerNameStore(state => state.updateCustomerName)
  const updateRoleName = useRoleNameStore(state => state.updateRoleName)

  const handleLogin = async (values) => {
    setLoading(true);
    setLoginError("");

    try {
      const { email, password } = values;
      const res = await authClient.post("auth/login", { email, password });

      if (res.first_login) {
        setEmail(email);
        setResetMode(false);
        setStage("waiting");
        handleForgotPassword(values)
      } else {
        const isMfaEnabled = res["is_mfa_enabled"] === 1;
        setEmail(email);
        updateCustomerLogo(res["customer_logo"])
        updateSmallCustomerLogo(res["small_customer_logo"])
        updateCustomerName(res["customer_name"]);
        updateRoleName(res["user_role_name"]);
        if (isMfaEnabled) {
          setStage("totpcode");
          return;
        } else {
          // If MFA not enabled, proceed to setup
          const resAuth = await apiClient.post("auth/mfa/setup", { email, password });
          generateQRCode(resAuth.secret, email);
          setStage("setupTotp");
          return;
        }
      }
    } catch (err) {
      setLoginError("Invalid credentials");
      message.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (values) => {
    setLoading(true);
    try {
      await authClient.post("auth/requestreset", { email: values.email });
      setEmail(values.email);
      setResetMode(true);
      setStage("waiting");
      message.info("Verification link sent to your email.");
    } catch (err) {
      message.error("Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (values) => {
    setLoading(true);
    try {
      const res = await authClient.post("auth/code", { email, code: values.code });
      if (res.message === "success") {
        setStage("newPassword");
        message.success("Code verified");
      } else {
        message.error("Invalid code");
      }

    } catch (err) {
      message.error("Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (values) => {
    setLoading(true);
    try {
      const res = await authClient.post("auth/newpassword", {
        email,
        hash: secure_hash,
        password: values.password,
      });
      message.success("Password set successfully");
      // Move to TOTP setup stage
      setStage("login");
      // Generate TOTP secret and QR code would be done here
      // generateQRCode(res.totpSecret, email);
    } catch (err) {
      message.error("Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (secret, email) => {
    try {
      const otpauthUrl = `otpauth://totp/SMS%20Portal%20${email}?secret=${secret}&issuer=SMS%20Portal`;
      const qrDataUrl = await QRCode.toDataURL(otpauthUrl);
      setQrCodeDataUrl(qrDataUrl);
      setTotpSecret(secret);
    } catch (error) {
      message.error("Failed to generate QR code");
    }
  };

  const handleSkipQRCode = () => {
    setStage("totpcode");
  };

   const handleVerifyTOTP = async (values) => {
      setLoading(true);
      setTotpError("");

      const payload = { 
        email: email || values.email,
        code: values.totp 
      };

      try {
        const res = await apiClient.post("auth/mfa/verify", payload);
        
        if (res["status"]) {
          updateCustomerLogo(res["customer_logo"])
          updateSmallCustomerLogo(res["small_customer_logo"])
          updateCustomerName(res["customer_name"]);
          if (isValidator) {
            navigate("/Events")
          } else {
            navigate("/deployments");
          }
        } else {
          setTotpError("Invalid TOTP code. Please check your authenticator app and try again.");
        }
      } catch (err) {
        setTotpError("Failed to verify TOTP code. Please try again.");
      } finally {
        setLoading(false);
      }
    };

  const currentYear = new Date().getFullYear();

  return (
    <div className="login-container">
      {/* Background image layer */}
      <div className="login-background" style={{ backgroundImage: 'url("/assets/construction.png")' }}/>
      
      {/* Optional: overlay for additional muting */}
      <div className="login-overlay" />

      {/* Your existing login card */}
      <div className="login-card">
        <img
          src="/assets/orbital-dark.png"
          alt="Company Logo"
          className="login-logo"
        />

        <Title level={3} className="login-title">
          Site Monitoring Service
        </Title>

        {/* LOGIN STAGE */}
        {stage === "login" && (
          <>
            <Text type="secondary">
              Sign in with your email and password.
            </Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleLogin}
              className="login-form"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  {
                    pattern: REGEX_PATTERNS.EMAIL,
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  placeholder="you@example.com"
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_EMAIL_CHARS, '');
                    form.setFieldValue('email', sanitized);
                  }}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: "Please enter your password" },
                  { whitespace: true, message: "Password cannot be only spaces" },
                ]}
              >
                <Input.Password placeholder="Password" />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="login-button"
              >
                Log In
              </Button>
            </Form>

            {loginError && (
              <div className="error-message">
                <Text style={{ color: "#d4380d" }}>
                  {loginError}
                </Text>
              </div>
            )}

            <div className="login-link-group">
              <Link onClick={() => setStage("forgotPassword")}>
                Forgot Password?
              </Link>
            </div>
          </>
        )}

        {/* FORGOT PASSWORD REQUEST STAGE */}
        {stage === "forgotPassword" && (
          <>
            <Text type="secondary">
              Enter your email to recieve a verification link.
            </Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleForgotPassword}
              className="login-form"
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: "Please enter your email" },
                  {
                    pattern: REGEX_PATTERNS.EMAIL,
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input
                  placeholder="you@example.com"
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_EMAIL_CHARS, '');
                    form.setFieldValue('email', sanitized);
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="login-button"
              >
                Send Link
              </Button>
            </Form>

            <div className="login-link-group">
              <Link onClick={() => setStage("login")}>Back to Login</Link>
            </div>
          </>
        )}

        {/* NEW PASSWORD STAGE */}

        {stage === "newPassword" && hash_timed_out && (
          <>
            <Title level={4} className="login-title">
              Password reset link is expired
            </Title>
            <div className="login-link-group">
              <Link onClick={() => setStage("login")}>Back to Login</Link>
            </div>
            </>
        )}

        {stage === "newPassword" && !hash_timed_out && (
          <>
            <Text type="secondary">
              {resetMode ? "Create a new password" : "Set your permanent password"}
            </Text>

            <Form
              layout="vertical"
              onFinish={handleNewPassword}
              className="login-form"
            >
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: "Please enter a password" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      const minLen = REGEX_PATTERNS.PASSWORD_MIN_LENGTH;
                      const upper = REGEX_PATTERNS.PASSWORD_UPPERCASE;
                      const lower = REGEX_PATTERNS.PASSWORD_LOWERCASE;
                      const number = REGEX_PATTERNS.PASSWORD_NUMBER;
                      const special = REGEX_PATTERNS.PASSWORD_SPECIAL_CHAR;

                      if (!minLen.test(value)) {
                        return Promise.reject("Must be at least 15 characters");
                      }
                      if (!upper.test(value)) {
                        return Promise.reject("Must include an uppercase letter");
                      }
                      if (!lower.test(value)) {
                        return Promise.reject("Must include a lowercase letter");
                      }
                      if (!number.test(value)) {
                        return Promise.reject("Must include a number");
                      }
                      if (!special.test(value)) {
                        return Promise.reject("Must include a special character");
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                hasFeedback
              >
                <Input.Password placeholder="Enter new password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm new password" />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="login-button"
              >
                Set Password
              </Button>
            </Form>
          </>
        )}

        {stage === "setupTotp" && qrCodeDataUrl && (
          <>
            <Title level={4} className="login-title setup-title">
              Set Up Two-Factor Authentication
            </Title>

            <Text type="secondary" className="qr-description">
              Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, etc.)
            </Text>

            <div className="qr-container">
              <img
                src={qrCodeDataUrl}
                alt="TOTP QR Code"
                className="qr-image"
              />
            </div>

            {totpSecret && (
              <div className="manual-entry-container">
                <Text type="secondary" className="manual-entry-label">
                  Can't scan the QR code? Enter this code manually:
                </Text>
                <Text
                  code
                  className="manual-entry-code"
                >
                  {totpSecret}
                </Text>
              </div>
            )}

            <Button
              type="primary"
              block
              className="login-button"
              onClick={handleSkipQRCode}
            >
              I've Scanned the QR Code
            </Button>
          </>
        )}

        {stage === "waiting" && (
          <>
            <Title level={4} className="login-title">
              Please check your email for your password reset link
            </Title>
            <Text type="secondary">
              You should recieve this email within one minute
            </Text>
            </>
        )}

        {stage === "totpcode" && (
          <>
            <Title level={4} className="login-title">
              Two-Factor Authentication
            </Title>

            {/* TOTP VERIFICATION STAGE */}
            <Text type="secondary">
              Enter the 6-digit code from your authenticator app
            </Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleVerifyTOTP}
              className="login-form"
            >
              <Form.Item
                name="totp"
                label="Authenticator Code"
                rules={[
                  { required: true, message: "Please enter your TOTP code" },
                  { len: 6, message: "Code must be 6 digits" },
                  { pattern: REGEX_PATTERNS.DIGITS_ONLY, message: "Code must contain only numbers" },
                ]}
              >
                <Input
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="off"
                  className="totp-input"
                  autoFocus
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(REGEX_PATTERNS.NON_DIGITS, '');
                    form.setFieldValue('totp', sanitized);
                  }}
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                className="login-button"
              >
                Verify Code
              </Button>
            </Form>

            {totpError && (
              <div className="error-message">
                <Text style={{ color: "#d4380d" }}>
                  {totpError}
                </Text>
              </div>
            )}

            <div className="login-link-group">
              <Link onClick={() => setStage("login")}>Back to Login</Link>
            </div>
          </>

        )}

        <div className="footer-links">
          <Link href='mailto:aiservices@orbitalengr.com'>Help</Link> | <Link href='/privacy'>Privacy Policy</Link> | <Link href='/terms'>Terms & Conditions</Link>
        </div>

        <div className="footer-copyright">
          © {currentYear} Orbital Engineering. All Rights Reserved. Orbital is
          a registered trademark of Orbital Engineering, Inc.
        </div>
      </div>
    </div>
  );
}